import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  LinearProgress,
  ListItemIcon
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Psychology as PsychologyIcon,
  ListAlt as ListAltIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Link as LinkIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { aiService } from '../services/api';

const steps = ['Description', 'Génération', 'Résultat'];

const analysisSteps = [
  { label: 'Analyse de la description du projet', icon: <PsychologyIcon />, duration: 1500 },
  { label: 'Identification des phases clés', icon: <TimelineIcon />, duration: 2000 },
  { label: 'Génération des tâches', icon: <ListAltIcon />, duration: 2500 },
  { label: 'Calcul des estimations', icon: <AssessmentIcon />, duration: 2000 },
  { label: 'Organisation des dépendances', icon: <LinkIcon />, duration: 1500 },
  { label: 'Optimisation du planning', icon: <TuneIcon />, duration: 1000 }
];

function GeneratePlanDialog({ open, onClose, onPlanGenerated }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    description: '',
    duration: '',
    projectType: '',
    team: ''
  });

  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [currentAnalysisStep, setCurrentAnalysisStep] = useState(0);
  const [completedAnalysisSteps, setCompletedAnalysisSteps] = useState([]);

  // Simulate analysis steps (boucle si le chargement dure plus longtemps)
  useEffect(() => {
    if (loading && activeStep === 1) {
      if (currentAnalysisStep < analysisSteps.length) {
        const timer = setTimeout(() => {
          setCompletedAnalysisSteps(prev => [...prev, currentAnalysisStep]);
          setCurrentAnalysisStep(prev => prev + 1);
        }, analysisSteps[currentAnalysisStep].duration);
        return () => clearTimeout(timer);
      } else {
        // Relancer l'animation si la réponse tarde
        const timer = setTimeout(() => {
          setCompletedAnalysisSteps([]);
          setCurrentAnalysisStep(0);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, activeStep, currentAnalysisStep]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    setError('');
  };

  const handleGenerate = async () => {
    if (!formData.description || !formData.duration) {
      setError('Veuillez remplir la description et la durée');
      return;
    }

    setLoading(true);
    setError('');
    setActiveStep(1);
    setCurrentAnalysisStep(0);
    setCompletedAnalysisSteps([]);

    try {
      const response = await aiService.generatePlan(formData);

      setGeneratedPlan(response.data.plan);
      setCompletedAnalysisSteps(analysisSteps.map((_, i) => i)); // Mark all as completed
      setActiveStep(2);
    } catch (error) {
      console.error('Generate plan error:', error);
      setError(error.response?.data?.error || 'Erreur lors de la génération du planning');
      setActiveStep(0);
    } finally {
      setLoading(false);
    }
  };

  const handleUse = () => {
    if (generatedPlan && onPlanGenerated) {
      onPlanGenerated(generatedPlan);
    }
    handleClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      description: '',
      duration: '',
      projectType: '',
      team: ''
    });
    setGeneratedPlan(null);
    setError('');
    setCurrentAnalysisStep(0);
    setCompletedAnalysisSteps([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 500
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <AutoAwesomeIcon />
        <Typography variant="h6" component="span">Générer avec ERA</Typography>
      </DialogTitle>

      <Stepper activeStep={activeStep} sx={{ pt: 3, px: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <DialogContent sx={{ pt: 3 }}>
        {/* Étape 1: Formulaire */}
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Décrivez votre projet et ERA génèrera automatiquement un planning complet avec toutes les tâches nécessaires.
              </Typography>
            </Alert>

            <TextField
              label="Description du projet"
              placeholder="Ex: Développer une application mobile de gestion de budget personnel"
              multiline
              rows={4}
              fullWidth
              required
              value={formData.description}
              onChange={handleChange('description')}
              helperText="Soyez le plus précis possible (10-500 caractères)"
            />

            <TextField
              label="Durée estimée"
              placeholder="Ex: 3 mois, 6 semaines, 12 jours"
              fullWidth
              required
              value={formData.duration}
              onChange={handleChange('duration')}
              helperText="Durée totale prévue pour le projet"
            />

            <TextField
              label="Type de projet (optionnel)"
              placeholder="Ex: PFE, Application web, Campagne marketing"
              fullWidth
              value={formData.projectType}
              onChange={handleChange('projectType')}
            />

            <TextField
              label="Taille de l'équipe (optionnel)"
              placeholder="Ex: 3"
              type="number"
              fullWidth
              value={formData.team}
              onChange={handleChange('team')}
              helperText="Nombre de personnes dans l'équipe"
            />

            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        )}

        {/* Étape 2: Génération en cours */}
        {activeStep === 1 && (
          <Box sx={{ minHeight: 350 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CircularProgress size={40} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  ERA génère votre planning...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(completedAnalysisSteps.length / analysisSteps.length) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 3
                    }
                  }}
                />
              </Box>
            </Box>

            <List dense sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 1 }}>
              {analysisSteps.map((step, index) => {
                const isCompleted = completedAnalysisSteps.includes(index);
                const isCurrent = index === currentAnalysisStep;

                return (
                  <ListItem
                    key={index}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      transition: 'all 0.3s',
                      bgcolor: isCurrent ? 'rgba(102, 126, 234, 0.08)' : 'transparent',
                      ...(isCompleted && {
                        bgcolor: 'rgba(76, 175, 80, 0.08)'
                      })
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {isCompleted ? (
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                      ) : isCurrent ? (
                        <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                      ) : (
                        React.cloneElement(step.icon, {
                          sx: { color: 'text.disabled' }
                        })
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={step.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: isCompleted ? 'success.main' : isCurrent ? 'primary' : 'text.secondary',
                        fontWeight: isCurrent ? 600 : 400
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              Cela peut prendre 10-20 secondes selon la complexité du projet
            </Typography>
          </Box>
        )}

        {/* Étape 3: Résultat */}
        {activeStep === 2 && generatedPlan && (
          <Box>
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                Planning généré avec succès !
              </Typography>
              <Typography variant="caption">
                {generatedPlan.tasks?.length || 0} tâches créées et organisées
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {generatedPlan.project?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {generatedPlan.project?.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Priorité: ${generatedPlan.project?.priority || 'Moyenne'}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`Durée: ${generatedPlan.project?.estimated_duration || formData.duration}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Paper>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 2 }}>
              Tâches générées ({generatedPlan.tasks?.length || 0})
            </Typography>

            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {generatedPlan.tasks?.map((task, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {task.title}
                            </Typography>
                            <Chip
                              label={task.phase}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Box>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {task.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip
                                label={`${task.duration_days || 1} jours`}
                                size="small"
                                sx={{ height: 18, fontSize: '0.6rem' }}
                              />
                              <Chip
                                label={`${task.estimated_hours || 8}h estimées`}
                                size="small"
                                sx={{ height: 18, fontSize: '0.6rem' }}
                              />
                              <Chip
                                label={task.priority}
                                size="small"
                                color={task.priority === 'urgent' || task.priority === 'high' ? 'error' : 'default'}
                                sx={{ height: 18, fontSize: '0.6rem' }}
                              />
                            </Box>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                    {index < generatedPlan.tasks.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>

        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !formData.description || !formData.duration}
            startIcon={<AutoAwesomeIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
              }
            }}
          >
            Générer avec ERA
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={handleUse}
            startIcon={<CheckCircleIcon />}
            color="success"
          >
            Utiliser ce planning
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default GeneratePlanDialog;
